/**
 * AI Agents API key management
 * Settings → User → AI Agents API Keys
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { PermissionService } from '@app/core/services/permission.service';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import {
  AiAgentChannel,
  AiAgentKey,
  AiAgentKeysService,
  CreatedAiAgentKey,
} from './ai-agent-keys.service';

const KEY_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

/** What a row's action button does: API keys are revoked for good, Keycloak clients are toggled. */
export type AiAgentKeyAction = 'revoke' | 'disable' | 'activate' | null;

@Component({
  selector: 'app-ai-agent-keys',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TagModule,
    ToastModule,
    MessageModule,
    AppButtonComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './ai-agent-keys.component.html',
  styleUrls: ['./ai-agent-keys.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAgentKeysComponent implements OnInit {
  private readonly service = inject(AiAgentKeysService);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.ai_agent_keys.create');
  });
  /** Covers revoke, disable and activate - all status changes. */
  canRevoke = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.ai_agent_keys.revoke');
  });

  readonly channelOptions: { label: string; value: AiAgentChannel }[] = [
    { label: 'ai_agent_keys.channel.ecommerce', value: 'ecommerce' },
    { label: 'ai_agent_keys.channel.backoffice', value: 'backoffice' },
    { label: 'ai_agent_keys.channel.pos', value: 'pos' },
  ];

  formatDateTime = formatDateTime;

  keys = signal<AiAgentKey[]>([]);
  loading = signal(false);

  showCreateDialog = signal(false);
  newKeyId = signal('');
  newChannel = signal<AiAgentChannel | null>(null);
  createSubmitted = signal(false);
  creating = signal(false);
  createError = signal<string | null>(null);
  /** Key IDs are lowercase only; typed capitals are shown lowered (CSS) and sent lowered. */
  private readonly normalizedKeyId = computed(() => this.newKeyId().trim().toLowerCase());
  keyIdInvalid = computed(() => !KEY_ID_PATTERN.test(this.normalizedKeyId()));
  channelMissing = computed(() => this.newChannel() === null);

  createdKey = signal<CreatedAiAgentKey | null>(null);

  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  private pending: { key: AiAgentKey; action: Exclude<AiAgentKeyAction, null> } | null = null;

  ngOnInit(): void {
    this.loadKeys();
  }

  loadKeys(): void {
    this.loading.set(true);
    this.service.list().subscribe({
      next: (keys) => {
        this.keys.set(keys);
        this.loading.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        this.showError(error, 'ai_agent_keys.messages.load_error');
      },
    });
  }

  /** The admin client is this screen's own caller, so it has no action here. */
  actionFor(key: AiAgentKey): AiAgentKeyAction {
    if (key.kind === 'API_KEY') {
      return key.status === 'ACTIVE' ? 'revoke' : null;
    }
    if (key.channel === 'admin') {
      return null;
    }
    return key.status === 'REVOKED' ? 'activate' : 'disable';
  }

  statusLabelKey(key: AiAgentKey): string {
    return key.kind === 'KEYCLOAK_CLIENT' && key.status === 'REVOKED'
      ? 'ai_agent_keys.status.DISABLED'
      : `ai_agent_keys.status.${key.status}`;
  }

  getStatusSeverity(status: string): 'success' | 'danger' | 'secondary' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'REVOKED') return 'danger';
    return 'secondary';
  }

  openCreateDialog(): void {
    this.newKeyId.set('');
    this.newChannel.set(null);
    this.createSubmitted.set(false);
    this.createError.set(null);
    this.showCreateDialog.set(true);
  }

  onKeyIdChange(value: string): void {
    this.newKeyId.set(value ?? '');
    this.createError.set(null);
  }

  onChannelChange(value: AiAgentChannel | null): void {
    this.newChannel.set(value);
    this.createError.set(null);
  }

  submitCreate(): void {
    this.createSubmitted.set(true);
    const channel = this.newChannel();
    if (this.keyIdInvalid() || channel === null || this.creating()) {
      return;
    }
    this.creating.set(true);
    this.createError.set(null);
    this.service.createApiKey(this.normalizedKeyId(), channel).subscribe({
      next: (created) => {
        this.creating.set(false);
        this.showCreateDialog.set(false);
        this.createdKey.set(created);
        this.loadKeys();
      },
      error: (error: HttpErrorResponse) => {
        this.creating.set(false);
        this.createError.set(this.translateError(error, 'ai_agent_keys.messages.create_error'));
      },
    });
  }

  copySecret(): void {
    const secret = this.createdKey()?.secret;
    if (!secret) {
      return;
    }
    navigator.clipboard
      .writeText(secret)
      .then(() =>
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('ai_agent_keys.messages.copied'),
        }),
      )
      .catch(() =>
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant('ai_agent_keys.messages.copy_error'),
        }),
      );
  }

  /** Drops the plaintext secret from memory; it cannot be shown again. */
  closeSecretDialog(): void {
    this.createdKey.set(null);
  }

  confirmAction(key: AiAgentKey): void {
    const action = this.actionFor(key);
    if (!action) {
      return;
    }
    this.pending = { key, action };
    this.confirmationTitle.set(this.translateService.instant(`ai_agent_keys.dialog.${action}_title`));
    this.confirmationMessage.set(
      this.translateService.instant(`ai_agent_keys.messages.confirm_${action}`, {
        keyId: key.keyId,
        channel: this.translateService.instant(`ai_agent_keys.channel.${key.channel}`),
      }),
    );
    this.confirmationButtons.set([
      { label: 'common.actions.cancel', action: 'cancel', outlined: true },
      {
        label: `ai_agent_keys.actions.${action}`,
        action: 'confirm',
        severity: action === 'activate' ? 'info' : 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(confirmation: string): void {
    this.showConfirmationDialog.set(false);
    const pending = this.pending;
    this.pending = null;
    if (confirmation !== 'confirm' || !pending) {
      return;
    }
    const request =
      pending.action === 'activate'
        ? this.service.activate(pending.key.keyId)
        : this.service.revoke(pending.key.keyId);
    request.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(`ai_agent_keys.messages.${pending.action}_success`),
        });
        this.loadKeys();
      },
      error: (error: HttpErrorResponse) =>
        this.showError(error, `ai_agent_keys.messages.${pending.action}_error`),
    });
  }

  private showError(error: HttpErrorResponse, fallbackKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateError(error, fallbackKey),
    });
  }

  private translateError(error: HttpErrorResponse, fallbackKey: string): string {
    const code = error?.error?.code;
    const key = code ? `ai_agent_keys.errors.${code}` : fallbackKey;
    const translated = this.translateService.instant(key);
    return translated === key ? this.translateService.instant(fallbackKey) : translated;
  }
}
